import { useState } from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

const faqs = [
  {
    question: "Which work can my team run directly?",
    answer:
      "LC Pack Review, Sanctions Screening, CBAM Readiness, and EUDR Readiness are self-serve. Each starts with a defined input and produces findings, citations, and an exportable result.",
  },
  {
    question: "When should we use a managed engagement?",
    answer:
      "Use managed intelligence when records are incomplete, data comes from several systems, exceptions need investigation, or your organization needs a reviewed delivery with clear ownership.",
  },
  {
    question: "Does a specialist review every software result?",
    answer:
      "No. Self-serve results are automated and reviewable by your team. A result is described as specialist reviewed only when a specialist has completed and recorded that review. Expert review can be added where available.",
  },
  {
    question: "What does a Verified Trade Decision Pack include?",
    answer:
      "Enso reconstructs one shipment across parties, order terms, documents, credentials, shipment facts, and payment evidence. The delivery records evidence gaps, exceptions, recommended actions, and a decision-ready pack. It is not a legal customs clearance.",
  },
  {
    question: "How are Duty Audit and Trade Audit different?",
    answer:
      "Duty Audit focuses on customs entries, duty treatment, supported recovery, and exposure. Trade Audit covers a wider population of transactions and company controls to identify recurring compliance failures and set a remediation plan.",
  },
  {
    question: "How is managed work priced?",
    answer:
      "Verified Trade Decision Pack is priced by shipment volume. Duty Audit is priced by data scope, with an optional fee on duty actually recovered. Trade Audit is priced by transaction volume, jurisdiction, business-unit scope, and assurance responsibility.",
  },
  {
    question: "Does TRDRHub clear a shipment or transaction?",
    answer:
      "No. TRDRHub provides checks, evidence, findings, and decision support. Customs authorities, banks, licensed brokers, and the responsible organization retain their legal and regulatory roles.",
  },
  {
    question: "How is our evidence handled?",
    answer:
      "The platform keeps source evidence linked to findings and records changes in the audit trail. Access is controlled by workspace and role. Contact us before an engagement if you need a specific retention, residency, or confidentiality arrangement.",
  },
];

export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="relative bg-[#00261C] py-24">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#B2F273]/30 to-transparent" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <div className="mb-14 text-center">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#B2F273]">
              Questions
            </p>
            <h2 className="mt-4 font-display text-4xl font-bold text-white sm:text-5xl">
              Clear scope before the work starts.
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div
                key={faq.question}
                className="overflow-hidden rounded-xl border border-[#EDF5F2]/10 bg-[#00382E]/45"
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="flex w-full items-center justify-between p-6 text-left text-white transition-colors hover:bg-[#00382E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#B2F273]"
                  aria-expanded={openIndex === index}
                >
                  <span className="pr-8 font-display font-semibold">{faq.question}</span>
                  <ChevronDown
                    className={cn(
                      "h-5 w-5 shrink-0 text-[#B2F273] transition-transform",
                      openIndex === index && "rotate-180",
                    )}
                  />
                </button>
                <div
                  className={cn(
                    "grid transition-all duration-200",
                    openIndex === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-6 leading-relaxed text-[#EDF5F2]/60">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
